// src/pages/intranet/pages/Inicio.jsx
import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Calendar, AlertCircle, FileText, Wallet, ArrowRight, Clock, CheckCircle } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

export default function Inicio() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [documentosRecientes, setDocumentosRecientes] = useState(0);
  const [estadoDeuda, setEstadoDeuda] = useState('Calculando...');
  const [actividadFeed, setActividadFeed] = useState([]);
  const [solicitudesPendientes, setSolicitudesPendientes] = useState(0);
  const [proximaTenida, setProximaTenida] = useState(null); // NUEVO ESTADO

  // Helper para el nivel numérico
  const getGradoNivel = (role) => {
    if (role === 'ADMIN') return 4;
    if (role === 'MAESTRO') return 3;
    if (role === 'COMPAÑERO') return 2;
    return 1;
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    if (user?.id) {
      cargarDashboard();
    }
  }, [user]);

  const cargarDashboard = async () => {
    try {
      const nivel = getGradoNivel(user?.role);
      const urlBase = 'https://logia-production-725b.up.railway.app/api';

      // 1. Cargar Documentos y Actividad
      const resDocs = await fetch(`${urlBase}/secretaria/documentos?grado_nivel=${nivel}`);
      const dataDocs = await resDocs.json();
      
      if (dataDocs.success) {
        const todosLosDocs = dataDocs.data;
        // Filtrar documentos de los últimos 7 días
        const hace7Dias = new Date();
        hace7Dias.setDate(hace7Dias.getDate() - 7);
        
        const recientes = todosLosDocs.filter(doc => new Date(doc.fecha_documento) >= hace7Dias);
        setDocumentosRecientes(recientes.length);
        
        // Tomar los últimos 3 para el feed de actividad
        setActividadFeed(todosLosDocs.slice(0, 3));
      }

      // 2. Cargar Estado de Cuenta del Usuario
      const resCuenta = await fetch(`${urlBase}/tesoreria/estado-cuenta?grado_nivel=${nivel}&usuario_id=${user.id}`);
      const dataCuenta = await resCuenta.json();
      
      if (dataCuenta.success && dataCuenta.data.length > 0) {
        const miCuenta = dataCuenta.data.find(c => c.id === user.id);
        if (miCuenta) {
          // Lógica simple para determinar si hay deuda
          const mesActual = new Date().getMonth() + 1; // 1 a 12
          const tieneMora = miCuenta.instalacion === 'pendiente' || 
                            miCuenta.aniversario === 'pendiente' || 
                            miCuenta.extra === 'pendiente' || 
                            miCuenta.mesesPagados < mesActual;
          
          setEstadoDeuda(tieneMora ? 'Con Deuda' : 'Al día');
        }
      }

      // 3. Cargar Solicitudes Pendientes (Solo para Maestros/Admins)
      if (user?.role === 'MAESTRO' || user?.role === 'ADMIN') {
        const resPendientes = await fetch(`${urlBase}/usuarios/pendientes`);
        const dataPendientes = await resPendientes.json();
        if (dataPendientes.success) {
          setSolicitudesPendientes(dataPendientes.data.length);
        }
      }

      // 4. Cargar Próxima Tenida / Evento (NUEVO)
      if (user?.logia_id) {
        const resEventos = await fetch(`${urlBase}/eventos/logia/${user.logia_id}`);
        const dataEventos = await resEventos.json();
        
        if (dataEventos.success && dataEventos.data.length > 0) {
          const ahora = new Date();
          // Filtrar eventos futuros y ordenar por el más próximo
          const eventosFuturos = dataEventos.data
            .filter(e => new Date(e.fecha_evento) > ahora)
            .sort((a, b) => new Date(a.fecha_evento) - new Date(b.fecha_evento));
          
          if (eventosFuturos.length > 0) {
            setProximaTenida(eventosFuturos[0]);
          }
        }
      }

    } catch (error) {
      console.error("Error cargando dashboard:", error);
    }
  };

  // Saludo dinámico según la hora del día
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Buenos días';
    if (hour < 19) return 'Buenas tardes';
    return 'Buenas noches';
  };

  return (
    <div className="flex flex-col gap-6 lg:gap-8 animate-fade-in">
      
      {/* BANNER DE BIENVENIDA */}
      <div className="relative bg-gradient-to-br from-[#0a0a0a] to-[#111111] border border-white/10 p-8 shadow-2xl flex flex-col md:flex-row justify-between md:items-center gap-6 overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')] opacity-10 mix-blend-overlay pointer-events-none"></div>
        <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-[#C5A059]/50 to-transparent"></div>

        <div className="relative z-10">
          <h1 className="font-serif text-3xl md:text-4xl text-white mb-2 tracking-wide">
            <span className="text-[#C5A059] italic pr-2">{getGreeting()},</span> 
            <br className="md:hidden" />
            R.·.H.·. {user?.username?.split(' ')[0] || 'Usuario'}
          </h1>
          <p className="text-gray-400 font-light text-sm md:text-base mt-2">
            Bienvenido al panel de control. Tu grado actual es <strong className="text-white font-medium tracking-wider uppercase text-xs">{user?.role}</strong>.
          </p>
        </div>

        <div className="relative z-10 bg-[#050505] border border-[#C5A059]/30 px-6 py-3 text-[#C5A059] text-[10px] md:text-xs tracking-[0.2em] uppercase font-semibold text-center whitespace-nowrap shadow-inner">
          {new Date().toLocaleDateString('es-PE', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
        </div>
      </div>

      {/* TARJETAS DE RESUMEN */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        
        {/* Próxima Tenida (DINÁMICA) */}
        <div className="bg-[#0a0a0a] border border-white/5 hover:border-[#C5A059]/30 p-6 shadow-lg transition-colors duration-300 flex flex-col gap-4">
          <div className="flex items-center gap-4">
            <div className="bg-[#050505] border border-white/10 p-3 text-[#C5A059]">
              <Calendar size={20} />
            </div>
            <span className="text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">Próxima Tenida</span>
          </div>
          <div>
            {proximaTenida ? (
              <>
                <h3 className="font-serif text-xl text-white m-0 mb-1 truncate" title={proximaTenida.titulo}>
                  {proximaTenida.titulo}
                </h3>
                <p className="text-gray-400 font-light text-sm m-0 capitalize">
                  {new Date(proximaTenida.fecha_evento).toLocaleString('es-PE', { 
                    weekday: 'long', 
                    day: 'numeric', 
                    month: 'long', 
                    hour: '2-digit', 
                    minute:'2-digit' 
                  })}
                </p>
              </>
            ) : (
              <>
                <h3 className="font-serif text-2xl text-white m-0 mb-1">Sin programar</h3>
                <p className="text-gray-400 font-light text-sm m-0">No hay eventos próximos en agenda</p>
              </>
            )}
          </div>
        </div>

        {/* Estado Financiero Dinámico */}
        <div className="bg-[#0a0a0a] border border-white/5 hover:border-[#C5A059]/30 p-6 shadow-lg transition-colors duration-300 flex flex-col gap-4">
          <div className="flex items-center gap-4">
            <div className="bg-[#050505] border border-white/10 p-3 text-green-500">
              <Wallet size={20} />
            </div>
            <span className="text-green-500 text-[10px] tracking-[0.2em] uppercase font-semibold">Estado de Cuenta</span>
          </div>
          <div>
            <h3 className={`font-serif text-2xl m-0 mb-1 ${estadoDeuda === 'Con Deuda' ? 'text-red-400' : 'text-white'}`}>
              {estadoDeuda}
            </h3>
            <p className="text-gray-400 font-light text-sm m-0">
              {estadoDeuda === 'Al día' ? 'Cotizaciones sin atraso' : 'Revisa Tesorería para ponerte al día'}
            </p>
          </div>
        </div>

        {/* Documentos Recientes Dinámico */}
        <div className="bg-[#0a0a0a] border border-white/5 hover:border-[#C5A059]/30 p-6 shadow-lg transition-colors duration-300 flex flex-col gap-4">
          <div className="flex items-center gap-4">
            <div className="bg-[#050505] border border-white/10 p-3 text-blue-400">
              <FileText size={20} />
            </div>
            <span className="text-blue-400 text-[10px] tracking-[0.2em] uppercase font-semibold">Secretaría</span>
          </div>
          <div>
            <h3 className="font-serif text-2xl text-white m-0 mb-1">
              {documentosRecientes} Nuevos
            </h3>
            <p className="text-gray-400 font-light text-sm m-0">Documentos añadidos los últimos 7 días</p>
          </div>
        </div>

      </div>

      {/* SECCIÓN INFERIOR: Actividad y Accesos Directos */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        
        {/* Panel Izquierdo: Actividad Reciente Dinámica */}
        <div className="lg:col-span-7 bg-[#0a0a0a] border border-white/5 p-6 md:p-8 shadow-xl">
          <h2 className="font-serif text-2xl text-white m-0 mb-6 border-b border-white/10 pb-4">Actividad de Secretaría</h2>
          
          <div className="flex flex-col gap-6">
            {actividadFeed.length > 0 ? (
              actividadFeed.map((doc, index) => (
                <div key={doc.id || index} className="flex gap-4 items-start">
                  <div className={`p-2 rounded-full mt-1 border ${doc.tipo_documento === 'acta' ? 'bg-green-500/10 text-green-500 border-green-500/20' : 'bg-blue-500/10 text-blue-400 border-blue-500/20'}`}>
                    {doc.tipo_documento === 'acta' ? <CheckCircle size={16} /> : <FileText size={16} />}
                  </div>
                  <div className="flex flex-col gap-1">
                    <p className="text-gray-300 font-light text-sm m-0 leading-relaxed">
                      Se registró: <strong className="text-white font-medium">{doc.asunto}</strong> {doc.numero_documento && `(${doc.numero_documento})`}
                    </p>
                    <span className="text-gray-500 text-xs font-mono tracking-widest uppercase">
                      {new Date(doc.fecha_documento).toLocaleDateString('es-PE')}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-gray-500 text-sm font-light italic">No hay actividad reciente para mostrar.</p>
            )}
          </div>
        </div>

        {/* Panel Derecho: Dinámico según Rol y Accesos */}
        <div className="lg:col-span-5 bg-[#0a0a0a] border border-white/5 p-6 md:p-8 shadow-xl">
          <h2 className="font-serif text-2xl text-white m-0 mb-6 border-b border-white/10 pb-4">Accesos Rápidos</h2>
          
          <div className="flex flex-col gap-4">
            {/* NUEVO ACCESO DIRECTO A ASISTENCIAS */}
            <Link to="/intranet/asistencia" className="group flex justify-between items-center bg-[#050505] border border-white/5 hover:border-[#C5A059]/50 p-4 transition-colors duration-300">
              <div>
                <h4 className="m-0 text-white font-medium text-sm mb-1 group-hover:text-[#C5A059] transition-colors">Registro de Asistencias</h4>
                <p className="m-0 text-gray-500 text-xs font-light tracking-wide">Accede al calendario de eventos</p>
              </div>
              <ArrowRight size={18} className="text-gray-600 group-hover:text-[#C5A059] transition-colors group-hover:translate-x-1" />
            </Link>

            <Link to="/intranet/secretaria" className="group flex justify-between items-center bg-[#050505] border border-white/5 hover:border-[#C5A059]/50 p-4 transition-colors duration-300">
              <div>
                <h4 className="m-0 text-white font-medium text-sm mb-1 group-hover:text-[#C5A059] transition-colors">Revisar Archivo</h4>
                <p className="m-0 text-gray-500 text-xs font-light tracking-wide">Accede a actas y documentos</p>
              </div>
              <ArrowRight size={18} className="text-gray-600 group-hover:text-[#C5A059] transition-colors group-hover:translate-x-1" />
            </Link>

            <Link to="/intranet/tesoreria" className="group flex justify-between items-center bg-[#050505] border border-white/5 hover:border-[#C5A059]/50 p-4 transition-colors duration-300">
              <div>
                <h4 className="m-0 text-white font-medium text-sm mb-1 group-hover:text-[#C5A059] transition-colors">Ver Tesorería</h4>
                <p className="m-0 text-gray-500 text-xs font-light tracking-wide">Consulta el balance de la logia</p>
              </div>
              <ArrowRight size={18} className="text-gray-600 group-hover:text-[#C5A059] transition-colors group-hover:translate-x-1" />
            </Link>

            {/* Este botón solo lo ven los Maestros y Admins */}
            {(user?.role === 'MAESTRO' || user?.role === 'ADMIN') && (
              <div className="mt-2 bg-gradient-to-br from-[#111111] to-[#0a0a0a] border border-[#C5A059]/30 p-5 flex flex-col gap-4 relative overflow-hidden">
                <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')] opacity-10 mix-blend-overlay"></div>
                <div className="relative z-10">
                  <h4 className="m-0 text-[#C5A059] font-serif text-lg mb-2">Panel de Control</h4>
                  <p className="m-0 text-gray-300 font-light text-sm leading-relaxed">
                    Tienes {solicitudesPendientes} solicitudes de acceso pendientes de revisión.
                  </p>
                </div>
                <button 
                  onClick={() => navigate('/intranet/datos-logia')}
                  className="relative z-10 bg-[#C5A059] hover:bg-white text-[#050505] border-none py-3 px-4 text-[10px] tracking-widest uppercase font-bold transition-colors cursor-pointer text-center w-full mt-2"
                >
                  Gestionar Miembros
                </button>
              </div>
            )}
          </div>
        </div>

      </div>
    </div>
  );
}